import React, {
  useCallback, useEffect, useMemo
} from 'react'
import { useQuery } from '@apollo/react-hooks'
import {
  isEmpty,
  isNil,
  pathOr,
  propOr
} from 'ramda'
import qs from 'query-string'

import ScreenView from './screen-view'
import { MODALS } from '../../constants'
import {
  getOverrides,
  getPlabackInfoIds,
  getScreenId,
  mapPlaybackInfoResponse,
  getAutoplayParams,
  isMovieScreen,
  isAutoplayDeeplink
} from '../../lib/screen'
import Loader from '../loader/index'
import NotFound from '../not-found/index'
import SCREEN_QUERY from '../../../graphql/queries/screen.gql'
import PLAYBACK_INFO_MANY_QUERY from '../../../graphql/queries/playback-info-many.gql'
import PLAYBACK_INFO_QUERY from '../../../graphql/queries/playback-info.gql'
import { getModalLocation } from '../../lib/modal'
import { MODALS_DO_NOT_CAUSE_SCREEN_RELOAD } from '../../layouts/routed-modal/routed-modal-registry'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import { useScreenWidth } from '../../hooks/use-screen-width'
import { useProfile } from '../../hooks/use-profile'
import { ScreenThemeProvider } from '../../hooks/use-screen-theme'

/**
 * Strip query params from a router location before building SCREEN_QUERY variables.
 *
 * @example
 * getLocationWithoutSearch({ pathname: '/my-lists', search: '?modal=shareList' })
 * // => { pathname: '/my-lists', search: '' }
 */
const getLocationWithoutSearch = ({ search, ...location }) => ({
  ...location,
  search: ''
})

/**
 * Return true when the underlying screen route changed.
 *
 * Opening, closing, or switching between modals that should keep the
 * underlying screen mounted should not count as a screen change, so search
 * strings are ignored when either side of the transition is one of those modals.
 */
const isScreenLocationChange = (currentProps, nextProps) => {
  const currentSearch = qs.parse(currentProps.location.search)
  const nextSearch = qs.parse(nextProps.location.search)
  const isNoReloadModalTransition = [
    currentSearch.modal,
    nextSearch.modal
  ].some(modal => MODALS_DO_NOT_CAUSE_SCREEN_RELOAD.includes(modal))
  const pathnameChanged = nextProps.location.pathname !== currentProps.location.pathname
  const searchChanged = qs.stringify(nextSearch) !== qs.stringify(currentSearch)
  const result = pathnameChanged || (!isNoReloadModalTransition && searchChanged)

  return result
}

// Ignore location changes caused by modals that do not require the screen to reload.
// Other prop changes and Apollo query result updates still render normally.
const shouldUpdateScreenQuery = (currentProps, nextProps) => {
  if (nextProps.location === currentProps.location) {
    return true
  }

  return isScreenLocationChange(currentProps, nextProps)
}

const ScreenContainer = React.memo((props) => {
  const {
    history,
    location
  } = props
  const isAuthenticated = useIsAuthenticated()
  const screenWidth = useScreenWidth()
  const addToMyList = useCallback(renderedInComponent => contentItem => history.push(getModalLocation(
    location,
    MODALS.qsParams.addContentToLists,
    {
      contentItemId: contentItem.id,
      renderedInComponent
    },
    {
      contentItem
    }
  )), [history, location])

  const {
    currentProfile,
    loading: profileLoading,
    error: profileError
  } = useProfile(undefined, { skip: !isAuthenticated })

  const profileId = currentProfile?.id || ''
  const isProfileReady = !isAuthenticated || (!profileLoading && !profileError)

  const screenQueryProps = useMemo(() => ({
    ...props,
    isAuthenticated,
    profileId,
    location: getLocationWithoutSearch(location)
  }), [isAuthenticated, location, profileId, props])

  const screenQuery = useQuery(SCREEN_QUERY, {
    skip: !isProfileReady,
    variables: {
      screenId: getScreenId(screenQueryProps),
      overrides: getOverrides(screenQueryProps)
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })
  const {
    data = {},
    loading,
    error,
    refetch
  } = screenQuery

  const shouldRedirectKidsToProfilesSwitch = isAuthenticated && location.pathname === '/kids'

  // Logged-in kids users must choose a profile before the kids screen renders.
  useEffect(() => {
    if (shouldRedirectKidsToProfilesSwitch) {
      history.push(getModalLocation({
        pathname: '/',
        search: location.search
      }, MODALS.qsParams.profilesSwitch))
    }
  }, [history, location, shouldRedirectKidsToProfilesSwitch])

  const isScreenMissing = isNil(data.screen)
  const isScreenReady = (
    !shouldRedirectKidsToProfilesSwitch &&
    isProfileReady &&
    !isScreenMissing &&
    !loading &&
    !error
  )

  const playbackInfoIds = useMemo(() => getPlabackInfoIds(data), [data])

  const playbackInfoManyQuery = useQuery(PLAYBACK_INFO_MANY_QUERY, {
    skip: !isAuthenticated || !isScreenReady || isEmpty(playbackInfoIds),
    variables: { contentItemIds: playbackInfoIds },
    fetchPolicy: 'network-only'
  })
  const playbackInfoMany = isAuthenticated && isScreenReady
    ? mapPlaybackInfoResponse(
      playbackInfoManyQuery.data && playbackInfoManyQuery.data.playbackInfoMany
    )
    : null

  // contentItemId from the autoplay deeplink is used for playbackInfoQuery.
  const contentItemId = useMemo(() => {
    const autoplayParams = getAutoplayParams(props)
    const seasonNumber = Number(pathOr(null, ['season'], autoplayParams))
    const episodeNumber = Number(pathOr(null, ['episode'], autoplayParams))
    let nextContentItemId = pathOr(null, ['screen', 'id'], data)

    // Series autoplay can target a specific season or episode from query params.
    if (seasonNumber) {
      const templateId = pathOr(null, ['screen', 'templateId'])(data)
      if (!templateId) return null

      const component = (pathOr([], ['screen', 'components'])(data)).find(c => c.__typename.toUpperCase() === `${templateId}COMPONENT`)
      if (!component) return null

      const seasonContentItem = (pathOr([], [templateId.toLowerCase(), 'seasons'])(component)).find(s => s.seasonNumber === seasonNumber)
      nextContentItemId = seasonContentItem ? seasonContentItem.id : null
      if (seasonContentItem && episodeNumber) {
        const episodeContentItem = pathOr([], ['episodes'])(seasonContentItem).find(e => e.episodeNumber === episodeNumber)
        nextContentItemId = episodeContentItem ? episodeContentItem.id : null
      }
    }

    return nextContentItemId
  }, [data, props])

  const isAutoplay = isAuthenticated && isAutoplayDeeplink(props)
  const hasAutoplayContentItem = !isNil(contentItemId) && isAutoplay
  const isMovieAutoplay = hasAutoplayContentItem && isMovieScreen(props)
  const shouldFetchPlaybackInfo = hasAutoplayContentItem && !isMovieAutoplay

  // Fetch playback info for series autoplay deeplinks so the target episode can be resolved.
  // Skip when no content item matched the deeplink, the deeplink is not autoplay, or the screen is a movie.
  const playbackInfoQueryResult = useQuery(PLAYBACK_INFO_QUERY, {
    skip: !isScreenReady || !shouldFetchPlaybackInfo,
    variables: {
      contentItemId
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
  const playbackInfo = isScreenReady && shouldFetchPlaybackInfo
    ? playbackInfoQueryResult.data?.playbackInfo
    : null

  // Movie autoplay can redirect immediately.
  // Series autoplay waits for playbackInfo to resolve the target episode.
  useEffect(() => {
    if (!hasAutoplayContentItem) {
      return
    }

    if (isMovieAutoplay) {
      history.push(`/watch/${contentItemId}`)
      return
    }

    const playbackContentItemId = propOr('', 'episodeToPlay', playbackInfo)
    if (playbackContentItemId) history.push(`/watch/${playbackContentItemId}`)
  }, [contentItemId, hasAutoplayContentItem, history, isMovieAutoplay, playbackInfo])

  if (shouldRedirectKidsToProfilesSwitch || !isProfileReady) {
    return null
  }

  if (isScreenMissing && !loading) {
    return <NotFound />
  }

  if (isScreenMissing || loading) {
    return <Loader />
  }

  if (error || hasAutoplayContentItem) {
    return null
  }

  const templateId = pathOr(null, ['screen', 'templateId'])(data)
  const isMarketing = !isNil(templateId) && templateId === 'MARKETING'
  const screenTitle = pathOr(null, ['screen', 'title'], data)

  return (
    <ScreenThemeProvider isWhiteTheme={false} isMarketing={isMarketing}>
      <ScreenView
        {...props}
        data={data}
        refetch={refetch}
        isContentItemOnMyList={() => false}
        addToMyList={addToMyList}
        playbackInfoMany={playbackInfoMany}
        isMarketing={isMarketing}
        screenTitle={screenTitle}
        screenWidth={screenWidth}
      />
    </ScreenThemeProvider>
  )
}, (currentProps, nextProps) => !shouldUpdateScreenQuery(currentProps, nextProps))

ScreenContainer.displayName = 'ScreenContainer'

export default ScreenContainer
